import ContentBox from '@components/contentBox';
import GridContainer from '@components/gridContainer';
import GridItem from '@components/gridItem';
import HelixImage from '@components/helixImage';
import HelixStack from '@components/helixStack';
import HelixLink from '@components/link';
import { List, ListItem, Typography, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTheme } from '@mui/system';
import React, { FC } from 'react';

type HelixFooterProps = {
  logo: {
    src: string;
    altText: string;
    href: string;
  };
  columns?: HelixFooterColumnProps[];
};

const HelixFooter: FC<HelixFooterProps> = ({ logo, columns = [] }) => {
  const theme = useTheme();
  const isBelowXL = useMediaQuery(theme.breakpoints.down('xl'));
  const columnSpacing = isBelowXL ? 3 : 8;
  const gridPadding = isBelowXL ? 3 : 6;
  const imageHeight = isBelowXL ? '30px' : '40px';

  return (
    <HelixFooterWrapper>
      <ContentBox>
        <GridContainer spacing={0} sx={{ paddingTop: gridPadding, paddingBottom: gridPadding }}>
          <GridItem md={2} sx={{ display: { xs: 'none', sm: 'none', md: 'block' } }}>
            <HelixLink href={logo.href}>
              <HelixImage
                imageBreakpoints={{ md: logo.src }}
                display="flex"
                height={imageHeight}
                altText={logo.altText}
              />
            </HelixLink>
          </GridItem>
          <GridItem xs={12} sm={12} md={10}>
            <HelixStack columnSpacing={columnSpacing} rowSpacing={4} direction="row" wrap={true}>
              {columns.map(({ items, header }) => (
                <HelixFooterColumn key={header} header={header} items={items} />
              ))}
            </HelixStack>
          </GridItem>
        </GridContainer>
      </ContentBox>
    </HelixFooterWrapper>
  );
};

const HelixFooterWrapper = styled('footer')(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
}));

type HelixFooterColumnProps = {
  header: string;
  items: JSX.Element[];
};

const HelixFooterColumn: FC<HelixFooterColumnProps> = ({ header, items }) => {
  return (
    <HelixStack spacing={1} sx={{ minWidth: '151px', maxWidth: '167px' }}>
      <Typography variant="caption" pb={1}>
        <strong>{header}</strong>
      </Typography>
      <List disablePadding>
        {items.map((item, index) => (
          <ListItem disableGutters key={index} sx={{ padding: '4px 0px' }}>
            {item}
          </ListItem>
        ))}
      </List>
    </HelixStack>
  );
};

export { HelixFooter as default, HelixFooter };
export type { HelixFooterColumnProps, HelixFooterProps };
